import {
    assertArray,
    assertFloat,
    assertIn,
    parseIntStrict,
} from '@/js/util/asserts.js';

import {
    ASSET_NAME,
    ASSET_TYPES,
    ASSET_TYPES_IN_PROJECT,
    AssetType,
    ATTRIBUTE_INTERNAL_NAMES_TO_NOT_AUTOPOPULATE,
    CM_PER_INCH,
    DEFAULT_ASSET_VALUES_AFTER_AUTOFILL,
    DEFAULT_COLOR_FIELD_VALUES,
    EXPORT_FIELD_PRETTY_NAMES,
    ExportField,
    HexColor,
    PROJECT_FEATURE_NAME,
    PROJECT_TYPES,
} from '@/js/util/shared_constants.js';

import { standalone_sha256 } from '@/js/util/standalone_sha256.js';
import { removeAllModals } from '@/js/classes/modal.js';

import { captureException as sentryCaptureException } from '@sentry/browser';


const stringToHexColor_cache = {};


// Page Functionality
export const jsRedirect = (url) => {
    console.log('>> Redirecting to `'+url+'`');
    removeAllModals();
    window.location.href = url;
}

export const jsOpenNewTab = (url) => {
    console.log('>> Opening `'+url+'` in new tab');
    window.open(url, '_blank');
}

export const jsRefresh = () => {
    removeAllModals();
    window.location.reload();
}

export const forceDownload = (fileName, fileContent, mimeType) => {
    const blob = new Blob([fileContent], { type: mimeType });
    const a = document.createElement('a');
    a.setAttribute('download', fileName);
    a.setAttribute('href', window.URL.createObjectURL(blob));
    a.click();
}

export const forceTextDownload = (fileName, fileContent) => {
    if(window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ event: 'download_file', fileName: fileName, fileContent: fileContent }));
    } else {
        forceDownload(fileName, fileContent, 'text/plain')
    }
}

export const getCacheAge = () => {
    trackCacheAge();
    return parseIntStrict(localStorage.getItem('newcollector_cache_age'));
}

export const trackCacheAge = () => {
    if(localStorage.getItem('newcollector_cache_age') ?? null) { return; }
    localStorage.setItem('newcollector_cache_age', currentUnixTimestamp().toString());
}

export const initServiceWorker = () => {
    if('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
            const sw_script_url = './'+BUILD_UID+'_sw.js';
            const sw_scope = './';
            navigator.serviceWorker.register(sw_script_url,  { scope: sw_scope }).then(registration => {
                console.log('Service Worker `'+sw_script_url+'` registered with scope `'+registration.scope+'`');
            }).catch(err => {
                console.log('Service Worker registration failed:', err);
                sentryCaptureException(err);
            });
        });
    } else {
        console.log('serviceWorker not in navigator');
    }
}

export const devModeTests = () => {
    if(build_mode === 'production') { return; }

    for(const asset_type of ASSET_TYPES) {
        if(asset_type === AssetType.Any) { continue; }
        assertIn(asset_type, ASSET_NAME);
    }

    for(const asset_type of ASSET_TYPES) {
        if(asset_type === AssetType.Any) { continue; }
        assertIn('default:'+ASSET_NAME[asset_type], DEFAULT_COLOR_FIELD_VALUES);
    }

    for(const project_type of PROJECT_TYPES) {
        assertIn(project_type, ASSET_TYPES_IN_PROJECT);
        assertIn(project_type, PROJECT_FEATURE_NAME);
        assertIn(project_type, DEFAULT_ASSET_VALUES_AFTER_AUTOFILL);
        assertIn(project_type, ATTRIBUTE_INTERNAL_NAMES_TO_NOT_AUTOPOPULATE);
    }

    const all_export_fields = Object.values(ExportField);
    for(const field of all_export_fields) {
        assertIn(field, EXPORT_FIELD_PRETTY_NAMES);
    }
}

export const getCookie = (name) => {
    let cookieValue = null;
    if(document.cookie && document.cookie !== '') {
        let cookies = document.cookie.split(';');
        for(let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i].trim()
            if(cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
                break;
            }
        }
    }
    return cookieValue;
}

export const getNodeFromString = (htmlString) => {
    const container = document.createElement('div');
    container.innerHTML = htmlString;
    return container.firstElementChild;
}

export const delay = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export const invertDict = (dict) => {
    return Object.fromEntries(
        Object.entries(dict).map(([key, value]) => [value, key])
    );
}

export const is_even = (num) => {
    assertFloat(num);
    return (num % 2 === 0);
}

export const chance = (percent) => {
    return (Math.random() < (percent/100));
}

export const getCanvasBlob = async (canvas, quality) => {
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            resolve(blob)
        }, 'image/jpeg', quality)
    })
}

export const resizeImage = (file) => {
    const MAX_SIZE_PIXELS = 800;
    const MAX_SIZE_MEGABYTES = 1;
    const JPEG_QUALITY = 1.00;
    const max_size_bytes = MAX_SIZE_MEGABYTES * 1000 * 1000;
    // stackoverflow.com/questions/23945494/
    return new Promise((resolve, reject) => {
        const imageUrl = URL.createObjectURL(file)
        let image = new Image()
        const input_file_size_megabytes = file.size / 1000 / 1000

        image.onload = async function (imageEvent) {
            let canvas = document.createElement('canvas')
            let width = image.width
            let height = image.height

            if(width > height) {
                if(width > MAX_SIZE_PIXELS) {
                    height *= MAX_SIZE_PIXELS / width
                    width = MAX_SIZE_PIXELS
                }
            } else {
                if(height > MAX_SIZE_PIXELS) {
                    width *= MAX_SIZE_PIXELS / height
                    height = MAX_SIZE_PIXELS
                }
            }

            canvas.width = width
            canvas.height = height
            canvas.getContext('2d').drawImage(image, 0, 0, width, height)

            let quality = JPEG_QUALITY
            let resized_file = await getCanvasBlob(canvas, quality)
            while(resized_file.size > max_size_bytes) {
                quality -= 0.02
                resized_file = await getCanvasBlob(canvas, quality)
            }
            const output_file_size_megabytes = resized_file.size / 1000 / 1000
            console.log('Compressed image with quality '+Math.round(quality*100)+', file size went from '+input_file_size_megabytes.toFixed(3)+' to '+output_file_size_megabytes.toFixed(3))
            URL.revokeObjectURL(imageUrl)
            resolve(resized_file)
        }

        image.onerror = function() {
            URL.revokeObjectURL(imageUrl)
            reject(new Error('Failed to load image in _resizeImage'))
        }

        image.src = imageUrl
    })
}

export const isBlankValue = (value) => {
    // This function should match "backend/lib/attributes/util.py is_blank_value"
    if(Array.isArray(value)) {
        if(value.length===0) {
            // Empty array is blank
            return true;

        } else if(value.every(inner_value => isBlankValue(inner_value))) {
            // Array only contains blank values
            return true;

        } else {
            // Non-empty & non-blank array
            return false;

        }

    } else {
        // Value is empty-string, undefined, or null
        return (value==='' || value==='null' || value===undefined || value===null);

    }
}

export const disableBrowserBackButton = () => {
    history.pushState(null, '!'+document.title, location.href);
    window.addEventListener('popstate', function () {
        history.pushState(null, '!'+document.title, location.href);
    });
}

export const fixDoubleSlashesInUrl = () => {
    const path = window.location.pathname;
    if(/\/{2,}/.test(path)) {
        const cleanedPath = path.replace(/\/{2,}/g, '/');
        const newUrl = window.location.origin + cleanedPath + window.location.search + window.location.hash;
        window.location.replace(newUrl);
        throw new Error('HALT_FOR_URL_REDIRECT');
    }
}

export const setPageTitle = (page_name) => {
    const collector_name = capitalizeFirstLetter(window.newcollector['current_collector_type']) + ' Collector';
    if(page_name==='') {
        document.title =  collector_name;
        return;
    }
    document.title = collector_name + ' | ' + page_name;
}

export const setHeaderProjectName = (project_id, subtitle) => {
    const subtitle_elem = document.getElementById('header-project-subtitle');
    if(subtitle_elem) {
        const main_title_elem = document.getElementById('header-title');
        const project_title_elem = document.getElementById('header-project');
        subtitle_elem.textContent = subtitle;
        subtitle_elem.href = 'assets.html?project_id='+project_id;
        main_title_elem.classList.add('hidden');
        project_title_elem.classList.remove('hidden');
    }
}

export const getPageFilename = () => {
    return (new URL(window.location.href)).pathname.split('/').pop();
}

export const promiseAllSettledConcurrently = async (tasks, num_concurrent = 5) => {
    const results = new Array(tasks.length);
    let currentIndex = 0;

    const worker = async () => {
        while(currentIndex < tasks.length) {
            const index = currentIndex++;
            const task = tasks[index];

            try {
                const value = await task();
                results[index] = { status: 'fulfilled', value };
            } catch(err) {
                results[index] = { status: 'rejected', err };
            }
        }
    }

    const workers = [];
    for(let i = 0; i < Math.min(num_concurrent, tasks.length); i++) { workers.push(worker()); }
    await Promise.allSettled(workers);
    return results;
}

export const getArrayWithoutValue = (val, arr) => {
    assertArray(arr);
    return arr.filter(item => item !== val);
}

export const deleteValueFromArray = (val, arr) => {
    assertArray(arr);
    for(let i = arr.length - 1; i >= 0; i--) {
        if(arr[i] === val) { arr.splice(i, 1); }
    }
}

export const highlightOnFirstClick = (input) => {
    let wasAlreadyFocused = false;

    input.addEventListener('mousedown', () => {
        wasAlreadyFocused = (document.activeElement === input);
    });

    input.addEventListener('click', () => {
        if(!wasAlreadyFocused) { input.select(); }
    });
}

export const isCtrlKeyDown = (e) => {
    return (e.ctrlKey || e.metaKey);
}

export const getIntersection = (...args) => {
    const validArrays = args.filter(Array.isArray);
    if(validArrays.length === 0) { return undefined; }
    if(validArrays.length === 1) { return [...new Set(validArrays[0])]; }

    validArrays.sort((a, b) => a.length - b.length);
    const intersection = new Set(validArrays[0]);
    for(let i = 1; i < validArrays.length; i++) {
        const currentSet = new Set(validArrays[i]);

        for(const item of intersection) {
            if(!currentSet.has(item)) { intersection.delete(item); }
        }

        if(intersection.size === 0) { break; }
    }

    return [...intersection];
}

export const getObjWithLimitedKeys = (obj, keys) => {
    const result = {};
    for(const key of keys) { if(key in obj) { result[key] = obj[key]; } }
    return result;
}

export const middleItemOfArray = (arr) => {
    const index = Math.floor(arr.length / 2);
    return arr[index];
}

export const isAnyInputFocused = () => {
    const active_elem = document.activeElement;
    if(!active_elem) { return false; }
    return active_elem.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(active_elem.tagName);
}


// String Manipulation
export const breakString = (input_string, max_length) => {
    if(!input_string) { return []; }
    if(max_length < 2) { throw new RangeError('max_length must be at least 2 to allow for hyphenation.'); }
    const result = [];

    while(input_string.length > 0) {
        if(input_string.length <= max_length) {
            result.push(input_string);
            break;
        }

        const chunk = input_string.slice(0, max_length);
        const last_comma_index = chunk.lastIndexOf(',');
        const last_space_index = chunk.lastIndexOf(' ');

        if(last_comma_index !== -1) {
            result.push(input_string.slice(0, last_comma_index));
            input_string = input_string.slice(last_comma_index + 1);
        } else if(last_space_index !== -1) {
            result.push(input_string.slice(0, last_space_index));
            input_string = input_string.slice(last_space_index + 1);
        } else {
            result.push(input_string.slice(0, max_length - 1) + '-');
            input_string = input_string.slice(max_length - 1);
        }
    }

    return result;
}

export const genId = (idLength = 24, charset = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ') => {
    const charsetLength = charset.length;
    const maxValidByte = 256 - (256 % charsetLength);
    const randomBytes = new Uint8Array(idLength*2);
    let result = '';

    while(result.length < idLength) {
        window.crypto.getRandomValues(randomBytes);
        for(let i = 0; i < randomBytes.length; i++) {
            const byte = randomBytes[i];
            if(byte < maxValidByte) {
                result += charset[byte % charsetLength];
                if(result.length === idLength) { return result; }
            }
        }
    }
    return result;
}

export const truncateString = (str, maxLength) => {
    if(str.length > maxLength) { return str.slice(0, maxLength - 3) + '...'; }
    return str;
}

export const toTitleCase = (str) => {
    const KNOWN_ACRONYMS = ['dbh'];
    if(Array.isArray(str)) {
        const ret = [];
        for(const inner_str of str) { ret.push(toTitleCase(inner_str)); }
        return ret;
    } else if(typeof str === "string") {
        return str.toLowerCase().replaceAll('_', ' ').split(' ').map(word => {
            if(KNOWN_ACRONYMS.includes(word)) { return word.toUpperCase(); }
            return word.charAt(0).toUpperCase() + word.slice(1);
        }).join(' ');
    } else {
        return '';
    }
}

export const capitalizeFirstLetter = (string) => {
    if(!string || typeof string !== 'string') { return ''; }
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export const toSnakeCase = (str) => {
    return str.replaceAll(' ', '_').toLowerCase();
}

export const breakCamelCase = (camelCaseString) => {
    return camelCaseString.replace(/([A-Z])/g, ' $1');
}

export const stripCharsFromEnd = (str, n) => {
    if(n < 0) { return str; }
    if(n >= str.length) { return ''; }
    return str.slice(0, str.length - n);
}

export const stripCharsFromBeginning = (str, n) => {
    if(n < 0) { return str; }
    return str.slice(n);
}

export const urlQuery = (params) => {
    return '?' + new URLSearchParams(params);
}

export const sha256 = async (message, hash_length = 48) => {
    if('subtle' in crypto) {
        const textEncoder = new TextEncoder();
        const data = textEncoder.encode(message);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, hash_length);
    } else {
        return standalone_sha256.hex(message).slice(0, hash_length);
    }
}

export const jsonStringifyForHashing = (obj) => {
    return JSON.stringify(obj, (key, value) => {
        if(value !== null && typeof value === 'object' && !Array.isArray(value)) {
            const sortedKeys = Object.keys(value).sort();
            const sortedObject = {};
            for(const sortedKey of sortedKeys) { sortedObject[sortedKey] = value[sortedKey]; }
            return sortedObject;
        }
        return value;
    });
}

export const base64url_encode = (str) => {
    const base64 = btoa(str);
    return base64
        .replaceAll('+', '-')
        .replaceAll('/', '_')
        .replaceAll('=', '');
};

export const base64url_decode = (str) => {
    let base64 = str
        .replaceAll('-', '+')
        .replaceAll('_', '/');

    while(base64.length % 4) { base64 += '='; }
    return atob(base64);
};

export const shallowJsonStringify = (obj) => {
    // Used to represent nested structures in error messages.
    return JSON.stringify(obj, (key, value) => {
        if(key === '') { return value; }
        if(typeof value === 'object' && value !== null) { return '[nested object]'; }
        if(typeof value === 'string') { return truncateString(value, 1024); }
        return value;
    });
};

export const toPascalCase = (str) => {
    if(!str) { return ''; }
    const noApostrophes = str.replace(/['’]/g, '');
    const words = noApostrophes.match(/[a-zA-Z0-9]+/g) || [];
    return words.map(word => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join('');
}

export const stringToHexColor = (str) => {
    if(!str || typeof str !== 'string') { return HexColor.Black; }
    if(str in stringToHexColor_cache) { return stringToHexColor_cache[str]; }
    let hash = 0;
    for(let i = 0; i < str.length; i++) { hash = str.charCodeAt(i) + ((hash << 5) - hash); }
    const hex = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    const ret = '#'+hex.padStart(6, '0');
    stringToHexColor_cache[str] = ret;
    return ret;
}

export const isDarkColor = (color) => {
    const hexRegex = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
    if(!hexRegex.test(color)) { return null; }

    let hex = color.replace('#', '');
    if(hex.length === 3) { hex = hex.split('').map(char => char + char).join(''); }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
}

export const hexToRgba = (hex, alpha) => {
    hex = hex.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const wrapText = (text, wrap_after) => {
    if(!text || wrap_after <= 0) { return text; }

    let result = '';
    let current_line_length = 0;
    let last_space_index = -1;

    for(let i = 0; i < text.length; i++) {
        const char = text[i];

        if(char === '\n') {
            result += char;
            current_line_length = 0;
            last_space_index = -1;

        } else {
            if(char === ' ') { last_space_index = result.length; }
            result += char;
            current_line_length++;

            if(current_line_length === wrap_after) {
                if(i + 1 < text.length && text[i + 1] !== '\n') {

                    const distance_to_last_space = (result.length - 1) - last_space_index;
                    if((last_space_index !== -1) && (distance_to_last_space <= 5)) {
                        result = result.substring(0, last_space_index)+'\n'+result.substring(last_space_index + 1);
                        current_line_length = distance_to_last_space;
                        last_space_index = -1;

                    } else {
                        result += '\n';
                        current_line_length = 0;
                        last_space_index = -1;
                    }
                }
            }
        }
    }

    return result;
}

export const createInlineStyledClone = (originalElement) => {
    const clone = originalElement.cloneNode(true);
    const sourceElements = [originalElement, ...originalElement.querySelectorAll('*')];
    const targetElements = [clone, ...clone.querySelectorAll('*')];

    const stylingProperties = [
        'color',
        'background-color',
        'font-family',
        'font-size',
        'font-weight',
        'font-style',
        'text-align',
        'vertical-align',
        'border-width',
        'border-style',
        'border-color',
        'border-collapse',
        'padding',
        'margin',
        'width',
        'height'
    ];

    sourceElements.forEach((sourceEl, index) => {
        const computedStyle = window.getComputedStyle(sourceEl);
        let inlineStyle = '';

        stylingProperties.forEach(prop => {
            const value = computedStyle.getPropertyValue(prop);

            if(value && value !== 'rgba(0, 0, 0, 0)' && value !== 'none' && value !== '0px') {
                inlineStyle += `${prop}: ${value}; `;
            }
        });

        targetElements[index].setAttribute('style', inlineStyle);
        targetElements[index].removeAttribute('class');
    });

    return clone;
}

// Timestamps
export const currentUnixTimestamp = () => {
    return Math.floor(Date.now() / 1000);
}

export const unixTimestampToReadableDate = (unixTimestampSeconds, opts = {}) => {
    unixTimestampSeconds = parseIntStrict(unixTimestampSeconds);
    if(unixTimestampSeconds === 631152000 || unixTimestampSeconds === 631152001) { return ''; }
    if(unixTimestampSeconds === 0) { return 'Never'; }
    const date = new Date(unixTimestampSeconds * 1000);
    const formatOptions = {};
    if(opts['showTimezone']) { formatOptions.timeZoneName = 'short'; }
    if(opts['use24Hour']) { formatOptions.hour12 = false; }
    if(opts['dateOnly']) { return date.toLocaleDateString(undefined, formatOptions); }
    if(opts['timeOnly']) { return date.toLocaleTimeString(undefined, formatOptions); }
    return date.toLocaleString(undefined, formatOptions);
}

export const unixTimestampToDateTimeLocal = (unixTimestampSeconds) => {
    const dateObject = new Date(unixTimestampSeconds * 1000);
    const year = dateObject.getFullYear();
    const month = (dateObject.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObject.getDate().toString().padStart(2, '0');
    const hours = dateObject.getHours().toString().padStart(2, '0');
    const minutes = dateObject.getMinutes().toString().padStart(2, '0');
    const seconds = dateObject.getSeconds().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}


// Unit Conversions
export const roundAwayFloatingPointInaccuracies = (number) => {
    return Math.round(number * 1000) / 1000;
}

export const inchesToCentimeters = (inches) => {
    return roundAwayFloatingPointInaccuracies(inches * CM_PER_INCH);
}

export const centimetersToInches = (centimeters) => {
    return roundAwayFloatingPointInaccuracies(centimeters / CM_PER_INCH);
}

export const isElementInViewport = (elem) => {
    const rect = elem.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}
